import { createSystem, defaultConfig, defineConfig, defineRecipe, defineSlotRecipe } from '@chakra-ui/react';
const colors = {
    bgMain: { value: '#EDEFF3' },
    bgSurface: { value: '#F3F5F7' },
    msg_2: { value: '#f4f5f7' },
    blue_01: { value: 'rgba(82, 108, 255, 0.10)' },
    blue_03: { value: 'rgba(82, 108, 255, 0.30)' },
    green_03: { value: 'rgba(164, 204, 188, 0.30)' },
    btnPrimary: { value: '#8F6BF9' },
    btnPrimaryBorder: { value: '#d9d4ea' },
    white: { value: '#fff' },
    iconsDisabled: { value: '#ACAABF' },
    textTertiary: { value: '#8C89A6' },
    textPrimary: { value: '#2C3D57' },
    textPrimaryLight: { value: '#415068' },
    textPrimarySuperLight: { value: '#566479' },
    textSecondary: { value: '#5A5680' },
    borderDefault: { value: '#E7E9EC' },
    borderAccent: { value: '#C9C9D7' },
    borderPrimaryLight: { value: '#C4C9ED' },
    grayLight: { value: '#eeeff3' },
    gray_1: { value: '#c8ccd4' },
    gray_3: { value: '#DFE2E6' },
    textLink: { value: '#526CFF' },
    btnPrimarySuperlight: { value: '#DDE2F4' },
    stateError: { value: '#F65751' },
    stateErrorLight: { value: 'rgba(246, 87, 81, 0.2)' },
    stateSuccess: { value: '#199F57' },
    otherPDF: { value: '#E72112' },
    otherDOC: { value: '#4A76E3' }
};
const shadows = {
    shadow_menu: { value: '0 6px 12px 0 rgba(90, 86, 128, 0.05)' },
    msg_1: { value: '0 6px 12px 0 rgba(90, 86, 128, 0.05)' },
    msg_2: { value: '0px 6px 12px 0px rgba(90, 86, 128, 0.05)' },
    modal: { value: '0px 24px 80px -24px rgba(67, 83, 96, 0.50)' },
    textarea: { value: '0 6px 12px 0 rgba(90, 86, 128, 0.05)' },
    textarea_focus: { value: '0px -4px 24px 0px rgba(116, 106, 251, 0.60)' },
    strong: { value: '0px -6px 16px 0px rgba(44, 61, 87, 0.30)' },
    thread: { value: '0 -6px 16px 0 rgba(44, 61, 87, 0.30)' }
};
const gradients = {
    accent: { value: 'linear-gradient(90deg, #516BFF 0%, #8F6BF9 88.94%, #8F6BF9 100%)' },
    msg_1: { value: 'linear-gradient(90deg, #516BFF 0%, #8F6BF9 88.94%, #8F6BF9 100%)' },
    textarea: { value: 'linear-gradient(90deg, #99a6f4 0%, #b3a0ed 88.94%, #b3a0ed 100%)' },
    textarea_focus: { value: 'linear-gradient(90deg, #516BFF 0%, #8F6BF9 88.94%, #8F6BF9 100%)' },
    media_controls: {
        value: 'linear-gradient(0deg, rgba(0, 0, 0, 0.60) 0%, rgba(0, 0, 0, 0.00) 100%)'
    },
    image_loading: {
        value: 'linear-gradient(135deg, #F3F5F7 0%, #EDEFF3 29.81%, #F3F5F7 60.42%, #EDEFF3 100%);'
    }
};
const buttonRecipe = defineRecipe({
    variants: {
        variant: {
            solid: {
                color: '{colors.white}',
                background: '{colors.textPrimary}',
                _hover: {
                    background: '{colors.textPrimary}'
                },
                _expanded: {
                    background: '{colors.textPrimary}'
                }
            },
            subtle: {
                color: '{colors.textPrimary}',
                background: '{colors.bgSurface}',
                _hover: {
                    background: '{colors.bgMain}'
                },
                _expanded: {
                    background: '{colors.bgMain}'
                }
            },
            success: {
                color: '{colors.stateSuccess}',
                background: '{colors.green_03}',
                _hover: {
                    background: '{colors.green_03}'
                },
                _expanded: {
                    background: '{colors.green_03}'
                }
            },
            danger: {
                color: '{colors.white}',
                background: '{colors.stateError}',
                _hover: {
                    background: '{colors.stateError}'
                },
                _expanded: {
                    background: '{colors.stateError}'
                }
            }
        },
        size: {
            lg: {
                minW: '160px',
                fontSize: '16px'
            }
        }
    }
});
const inputRecipe = defineRecipe({
    variants: {
        variant: {
            outline: {
                background: 'white',
                border: 'thin solid',
                borderColor: '{colors.bgMain}',
                _hover: {
                    borderColor: '{colors.textPrimarySuperLight}'
                },
                _focus: {
                    borderColor: '{colors.textPrimary} !important'
                },
                _disabled: {
                    opacity: 0.8
                }
            }
        }
    }
});
const textareaRecipe = defineRecipe({
    variants: {
        variant: {
            outline: {
                background: 'white',
                border: 'thin solid',
                borderColor: '{colors.bgMain}',
                _hover: {
                    borderColor: '{colors.textPrimarySuperLight}'
                },
                _focus: {
                    borderColor: '{colors.textPrimary}'
                },
                _disabled: {
                    opacity: 0.8
                }
            }
        }
    }
});
const checkboxRecipe = defineSlotRecipe({
    slots: ['root', 'control', 'label'],
    variants: {
        variant: {
            solid: {
                control: {
                    borderColor: '{colors.bgMain}',
                    outline: 'none',
                    _hover: {
                        borderColor: '{colors.textPrimarySuperLight}',
                        cursor: 'pointer'
                    },
                    '&:is([data-state=checked], [data-state=indeterminate])': {
                        background: '{colors.textPrimary}',
                        borderColor: '{colors.textPrimary} !important',
                        color: '{colors.white}'
                    }
                }
            }
        },
        size: {
            lg: {
                control: {
                    _icon: {
                        boxSize: '3.5'
                    }
                }
            }
        }
    },
    defaultVariants: {
        variant: 'solid'
    }
});
const linkRecipe = defineRecipe({
    variants: {
        variant: {
            plain: {
                color: '{colors.textLink}',
                _hover: {
                    textDecoration: 'none'
                }
            }
        }
    }
});
const config = defineConfig({
    strictTokens: false,
    globalCss: {
        'html, body, #root': {
            margin: 0,
            padding: 0,
            fontWeight: 500,
            fontSize: '16px',
            overflow: 'hidden',
            color: '{colors.textSecondary}',
            background: '{colors.bgMain}',
            fontFamily: 'AvenirNext, system-ui, sans-serif'
        }
    },
    theme: {
        breakpoints: {
            base: '0', // 0px
            sm: '15rem', // 240px
            md: '48rem', // 768px
            lg: '64rem', // 1024px
            xl: '96rem', // 1536px
            '2xl': '120rem' // 1920px
        },
        tokens: {
            colors,
            shadows,
            gradients
        },
        recipes: {
            link: linkRecipe,
            input: inputRecipe,
            button: buttonRecipe,
            textarea: textareaRecipe
        },
        slotRecipes: {
            checkbox: checkboxRecipe
        }
    }
});
// Extends default theme
export const systemTheme = createSystem(defaultConfig, config);
//# sourceMappingURL=theme.js.map